/**
 * 统一缓存服务
 * 薄DDD方案 - 提供统一的缓存机制，避免重复API调用
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl?: number;
}

interface CacheOptions {
  ttl?: number; // 过期时间（毫秒）
  key?: string; // 自定义缓存键
  useMemory?: boolean; // 是否使用内存缓存
}

import { createManagedCache, LRUCache } from "./LRUCache";
import { createModuleLogger } from "./Logger";

const logger = createModuleLogger("CacheService");

/**
 * 缓存服务
 */
export class CacheService {
  private static instance: CacheService;
  private memoryCache: Map<string, CacheEntry<any>>;
  private lruCache: LRUCache<any>;
  private pendingFetches = new Map<string, Promise<any>>();
  private listeners = new Set<() => void>();

  private constructor() {
    // 使用LRU缓存
    this.lruCache = createManagedCache("main_cache", 1000, 60000);

    // 保留内存缓存用于临时数据
    this.memoryCache = new Map();

    // 定期清理过期缓存
    setInterval(() => this.cleanup(), 60000); // 每分钟清理一次
  }

  /**
   * 获取单例实例
   */
  static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  /**
   * 获取缓存数据
   */
  async get<T>(
    key: string,
    fetchFn?: () => Promise<T>,
    options?: CacheOptions,
  ): Promise<T | null> {
    const finalKey = options?.key || key;

    const entry = this.memoryCache.get(finalKey);

    if (entry && !this.isExpired(entry)) {
      return entry.data;
    }

    if (!entry) {
      const lruValue = this.lruCache.get(finalKey);
      if (lruValue !== null) {
        return lruValue;
      }
    }

    // 如果有fetch函数，获取新数据
    if (fetchFn) {
      const pendingFetch = this.pendingFetches.get(finalKey);
      if (pendingFetch) {
        return pendingFetch;
      }

      const request = (async () => {
        try {
          const data = await fetchFn();
          this.set(finalKey, data, options);
          return data;
        } catch (error) {
          // 如果缓存过期但获取失败，返回旧数据（如果可用）
          if (entry) {
            logger.warn(`获取缓存 ${finalKey} 失败，使用过期数据:`, error);
            return entry.data;
          }
          logger.warn(`获取缓存 ${finalKey} 失败:`, error);
          return null;
        } finally {
          this.pendingFetches.delete(finalKey);
        }
      })();

      this.pendingFetches.set(finalKey, request);
      return request;
    }

    if (entry && this.isExpired(entry)) {
      this.memoryCache.delete(finalKey);
      this.lruCache.delete(finalKey);
    }

    return null;
  }

  /**
   * 设置缓存数据
   */
  set<T>(key: string, data: T, options?: CacheOptions): void {
    const finalKey = options?.key || key;

    this.storeEntry(finalKey, data, options);

    if (options?.key && options.key !== key) {
      this.storeEntry(key, data, options);
    }

    this.notifyListeners();
  }

  private storeEntry<T>(key: string, data: T, options?: CacheOptions): void {
    // 存储到LRU缓存
    this.lruCache.set(key, data);

    // 同时存储到内存缓存（包含元数据）
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: options?.ttl,
    };

    this.memoryCache.set(key, entry);
  }

  /**
   * 删除缓存
   */
  delete(key: string, options?: CacheOptions): void {
    const finalKey = options?.key || key;
    this.memoryCache.delete(finalKey);
    this.lruCache.delete(finalKey);
    this.pendingFetches.delete(finalKey);
    this.notifyListeners();
  }

  /**
   * 清空缓存
   */
  clear(): void {
    this.memoryCache.clear();
    this.lruCache.clear();
    this.pendingFetches.clear();
    this.notifyListeners();
  }

  /**
   * 获取缓存统计
   */
  getDetailedStats(): {
    memory: {
      total: number;
      hit: number;
      miss: number;
      hitRate: number;
      expired: number;
    };
    lru: {
      size: number;
      maxSize: number;
      hitRate: number;
    };
    totalHits: number;
    totalMisses: number;
  } {
    // 内存缓存统计
    let memoryHit = 0;
    let memoryMiss = 0;
    let memoryExpired = 0;

    this.memoryCache.forEach((entry) => {
      if (this.isExpired(entry)) {
        memoryExpired++;
      } else {
        memoryHit++;
      }
    });

    memoryMiss = this.memoryCache.size - memoryHit - memoryExpired;

    // LRU缓存统计
    const lruStats = this.lruCache.getStats();

    return {
      memory: {
        total: this.memoryCache.size,
        hit: memoryHit,
        miss: memoryMiss,
        hitRate:
          this.memoryCache.size > 0 ? memoryHit / this.memoryCache.size : 0,
        expired: memoryExpired,
      },
      lru: {
        size: lruStats.size,
        maxSize: lruStats.maxSize,
        hitRate: lruStats.hitRate,
      },
      totalHits: memoryHit + lruStats.hitCount,
      totalMisses: memoryMiss + lruStats.missCount,
    };
  }

  /**
   * 获取缓存统计
   */
  getStats(): {
    total: number;
    hit: number;
    miss: number;
    hitRate: number;
    expired: number;
  } {
    let hit = 0;
    let miss = 0;
    let expired = 0;

    this.memoryCache.forEach((entry) => {
      if (this.isExpired(entry)) {
        expired++;
      } else {
        hit++;
      }
    });

    miss = this.memoryCache.size - hit - expired;

    return {
      total: this.memoryCache.size,
      hit,
      miss,
      hitRate: this.memoryCache.size > 0 ? hit / this.memoryCache.size : 0,
      expired,
    };
  }

  /**
   * 缓存装饰器 - 自动缓存函数结果
   */
  memoize<T extends (...args: unknown[]) => any>(
    fn: T,
    options?: CacheOptions & {
      keyGenerator?: (...args: Parameters<T>) => string;
    },
  ): T {
    const memoizedFn = async (
      ...args: Parameters<T>
    ): Promise<ReturnType<T>> => {
      const key = options?.keyGenerator
        ? options.keyGenerator(...args)
        : `${fn.name}_${JSON.stringify(args)}`;

      const result = await this.get(key, () => fn(...args), options);
      if (result === null) {
        // Fallback: call function directly if cache miss
        return fn(...args);
      }
      return result as ReturnType<T>;
    };

    return memoizedFn as T;
  }

  /**
   * 批量缓存装饰器
   */
  batchMemoize<T extends (...args: unknown[]) => Promise<any[]>>(
    fn: T,
    options?: CacheOptions & {
      keyGenerator?: (...args: Parameters<T>) => string;
      batchKey?: string;
    },
  ): T {
    const batchCache = new Map<string, any[]>();
    const lastFetchTime = new Map<string, number>();

    const memoizedFn = async (
      ...args: Parameters<T>
    ): Promise<ReturnType<T>> => {
      const batchKey = options?.batchKey || `${fn.name}_batch`;
      const itemKey = options?.keyGenerator
        ? options.keyGenerator(...args)
        : JSON.stringify(args);

      // 检查是否已在批量请求中
      const cachedBatch = batchCache.get(batchKey);
      if (
        cachedBatch &&
        Date.now() - (lastFetchTime.get(batchKey) || 0) <
          (options?.ttl || 30000)
      ) {
        const item = cachedBatch.find(
          (item: Record<string, unknown>) =>
            JSON.stringify(item) === itemKey ||
            (typeof item.id === "string" &&
              item.id === (args[0] as Record<string, unknown>)?.id),
        );
        if (item) {
          return [item] as ReturnType<T>;
        }
      }

      // 执行批量查询
      const results = await fn(...args);

      // 更新批量缓存
      batchCache.set(batchKey, results);
      lastFetchTime.set(batchKey, Date.now());

      // 找到匹配的结果
      const matchingResult = results.find(
        (item: Record<string, unknown>) =>
          JSON.stringify(item) === itemKey ||
          (typeof item.id === "string" &&
            item.id === (args[0] as Record<string, unknown>)?.id),
      );

      return (matchingResult ? [matchingResult] : []) as ReturnType<T>;
    };

    return memoizedFn as unknown as T;
  }

  /**
   * 监听缓存变化
   */
  onChange(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * 检查缓存是否过期
   */
  private isExpired(entry: CacheEntry<any>): boolean {
    if (!entry.ttl) return false;
    return Date.now() - entry.timestamp > entry.ttl;
  }

  /**
   * 清理过期缓存
   */
  private cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    this.memoryCache.forEach((entry, key) => {
      if (entry.ttl && now - entry.timestamp > entry.ttl) {
        expiredKeys.push(key);
      }
    });

    expiredKeys.forEach((key) => this.memoryCache.delete(key));
  }

  /**
   * 通知监听器
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        logger.error("缓存监听器错误:", error);
      }
    });
  }
}

/**
 * 便捷函数
 */
export const cache = CacheService.getInstance();

/**
 * 创建缓存键
 */
export function createCacheKey(prefix: string, params?: unknown): string {
  if (!params) return prefix;
  return `${prefix}_${JSON.stringify(params)}`;
}

/**
 * 带过期时间的缓存
 */
export async function withExpiry<T>(
  key: string,
  ttl: number,
  fetchFn: () => Promise<T>,
): Promise<T> {
  const result = await cache.get(key, fetchFn, { ttl });
  if (result === null) {
    return fetchFn();
  }
  return result;
}

/**
 * 内存缓存装饰器
 */
export function memoizeCache(
  options?: CacheOptions & { keyGenerator?: (...args: unknown[]) => string },
): MethodDecorator {
  return (
    _target: Object,
    _propertyKey: string | symbol,
    descriptor: PropertyDescriptor,
  ) => {
    const originalMethod = descriptor.value;

    if (originalMethod) {
      descriptor.value = cache.memoize(originalMethod, options) as any;
    }

    return descriptor;
  };
}

/**
 * 批量缓存装饰器
 */
export function batchMemoizeCache(
  options?: CacheOptions & {
    keyGenerator?: (...args: unknown[]) => string;
    batchKey?: string;
  },
): MethodDecorator {
  return (
    _target: Object,
    _propertyKey: string | symbol,
    descriptor: PropertyDescriptor,
  ) => {
    const originalMethod = descriptor.value;

    if (originalMethod) {
      descriptor.value = cache.batchMemoize(originalMethod, options) as any;
    }

    return descriptor;
  };
}
